import { useState, useEffect, useRef } from 'react';
import type { Language, SourceKind } from '@sourcr/shared';
import { SOURCES, SOURCE_LABELS, SOURCE_KIND } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { SourceBadge } from '../../components/source-badge';
import { getUserSettings, updateUserSettings } from '../../lib/api';

interface SettingsPageProps {
  lang: Language;
  theme: 'dark' | 'light';
  onSetLang: (l: Language) => void;
  onToggleTheme: () => void;
}

const S = {
  page: { display: 'flex', flexDirection: 'column' as const, gap: 24, maxWidth: 640 },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  section: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  sectionDesc: { fontSize: 12, color: 'var(--text-muted)' },
  row: {
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid var(--border)',
  },
  rowLast: {
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggle: {
    position: 'relative' as const,
    width: 40,
    height: 22,
    cursor: 'pointer',
  },
  toggleTrack: (on: boolean): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    borderRadius: 11,
    backgroundColor: on ? 'var(--accent)' : 'var(--bg-overlay)',
    transition: 'background 0.2s',
    border: '1px solid var(--border)',
  }),
  toggleThumb: (on: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: 2,
    left: on ? 20 : 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: on ? '#fff' : 'var(--text-muted)',
    transition: 'left 0.2s',
  }),
  segBtn: (active: boolean): React.CSSProperties => ({
    padding: '5px 14px',
    borderRadius: 7,
    border: '1px solid var(--border)',
    backgroundColor: active ? 'var(--accent-subtle)' : 'var(--bg-base)',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.12s',
  }),
  segGroup: { display: 'flex', gap: 6 },
  saveError: {
    fontSize: 12,
    color: 'var(--danger)',
    padding: '6px 20px',
  },
  sourceLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  kindChip: (kind: SourceKind): React.CSSProperties => {
    const color =
      kind === 'live-official' || kind === 'live-own'
        ? 'var(--success)'
        : kind === 'assist' || kind === 'public-beta'
          ? 'var(--accent)'
          : 'var(--warning)';
    return {
      fontFamily: 'var(--font-mono)',
      fontSize: 9,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color,
      border: `1px solid color-mix(in oklch, ${color} 40%, var(--border))`,
      borderRadius: 4,
      padding: '1px 6px',
      whiteSpace: 'nowrap',
    };
  },
};

function kindLabel(
  i: {
    kindLiveOfficial: string;
    kindAssist: string;
    kindLiveOwn: string;
    kindPublicBeta: string;
    kindDemo: string;
  },
  kind: SourceKind,
): string {
  switch (kind) {
    case 'live-official': return i.kindLiveOfficial;
    case 'assist': return i.kindAssist;
    case 'live-own': return i.kindLiveOwn;
    case 'public-beta': return i.kindPublicBeta;
    default: return i.kindDemo;
  }
}

export function SettingsPage({ lang, theme, onSetLang, onToggleTheme }: SettingsPageProps) {
  const i = I18N[lang].settings;
  const [enabledSources, setEnabledSources] = useState<Set<string>>(new Set(SOURCES));
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Debounce timer ref — cleared on each toggle, fires 500ms after last change
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load settings on mount
  useEffect(() => {
    let cancelled = false;
    getUserSettings()
      .then((data) => {
        if (!cancelled) {
          setEnabledSources(new Set(data.enabledSources));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // On fetch failure, keep defaults (all enabled)
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  function toggleSource(src: string) {
    setSaveError(null);
    setEnabledSources((s) => {
      const next = new Set(s);
      if (next.has(src)) {
        next.delete(src);
      } else {
        next.add(src);
      }

      // Debounced persist — 500ms after last toggle
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        updateUserSettings({ enabledSources: [...next] }).catch(() => {
          setSaveError('Failed to save settings. Changes will be lost on refresh.');
        });
      }, 500);

      return next;
    });
  }

  return (
    <div style={S.page}>
      <h1 style={S.title}>{i.title}</h1>

      {/* Data sources */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>{i.sources}</h2>
          <span style={S.sectionDesc}>{i.sourcesDescription}</span>
        </div>
        {loading ? (
          <div style={{ padding: '14px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
            Loading...
          </div>
        ) : (
          SOURCES.map((src, idx) => {
            const isLast = idx === SOURCES.length - 1;
            const isOn = enabledSources.has(src);
            return (
              <div key={src} style={isLast ? S.rowLast : S.row}>
                <span style={S.sourceLeft}>
                  <SourceBadge sourceId={src} showLabel />
                  <span style={S.kindChip(SOURCE_KIND[src])} title={kindLabel(i, SOURCE_KIND[src])}>
                    {kindLabel(i, SOURCE_KIND[src])}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => toggleSource(src)}
                  style={S.toggle}
                  role="switch"
                  aria-checked={isOn}
                  aria-label={`Toggle ${SOURCE_LABELS[src]}`}
                >
                  <div style={S.toggleTrack(isOn)} />
                  <div style={S.toggleThumb(isOn)} />
                </button>
              </div>
            );
          })
        )}
        {saveError !== null && <div style={S.saveError}>{saveError}</div>}
      </div>

      {/* Appearance */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>{i.appearance}</h2>
        </div>

        {/* Language */}
        <div style={S.row}>
          <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{i.language}</span>
          <div style={S.segGroup}>
            {(['en', 'uk'] as Language[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => onSetLang(l)}
                style={S.segBtn(lang === l)}
              >
                {l === 'en' ? 'English' : 'Українська'}
              </button>
            ))}
          </div>
        </div>

        {/* Theme */}
        <div style={S.rowLast}>
          <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{i.theme}</span>
          <div style={S.segGroup}>
            {(['dark', 'light'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { if (theme !== t) onToggleTheme(); }}
                style={S.segBtn(theme === t)}
              >
                {t === 'dark' ? i.dark : i.light}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
